import { Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { Fragment } from "react";
import {
  PrimaryDialog,
  StyledButton,
  WarningCustomIcon,
} from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  ACCOUNT_CREATION_ROUTE,
  CANCEL,
  CANCEL_BUTTON,
  CONTACT_NUMBER,
  CONTAINED,
  FIND_PROVIDER_URL,
  OUTLINED,
  PROVIDER_CONTACT_NUMBER,
  TARGET_BLANK,
  TEXT,
  WARNING_ICON_ID,
} from "../../../Constants/CommonConstants";
import {
  CREATE_NEW_ACCOUNT_BUTTON,
  CREATE_NEW_APPLICATION_BUTTON,
  FIND_A_PROVIDER,
  FIND_A_PROVIDER_BUTTON,
  FLOW_TEXT_ACCOUNT,
  FLOW_TEXT_APPLICATION,
  NO_MATCH_FOUND_MODAL,
  NO_MATCH_FOUND_MODAL_MODAL_CONTENT_1,
  NO_MATCH_FOUND_MODAL_TITLE,
} from "../../../Constants/NoMatchFoundModalConstants";
import ConfigService from "../../../Services/ConfigService";
import EndpointService from "../../../Services/EndpointService";

/**
 * @description No Match Found Modal
 * @param {string} flow represent whether the modal is opening in "Forgot PW" flow (account) or "link an application flow" (application)
 * @param {boolean} open Boolean flag to show the modal/dialog
 * @param {function} setOpen Setter method to update the boolean flag
 * @returns {ReactNode} Renders the dialog to the user.
 */
const NoMatchFoundModal = ({ flow, open, setOpen }) => {
  const intl = useIntl();

  const navigate = useNavigate();

  const flowText =
    flow === FLOW_TEXT_ACCOUNT ? FLOW_TEXT_ACCOUNT : FLOW_TEXT_APPLICATION;

  const contactNumber =
    flow === FLOW_TEXT_ACCOUNT
      ? ConfigService(CONTACT_NUMBER)
      : ConfigService(PROVIDER_CONTACT_NUMBER);

  const onCreateNewApplicationButtonClickHandler = () => {
    setOpen(false);
    navigate(`/${ACCOUNT_CREATION_ROUTE}`);
  };
  const onCancelButtonClickHandler = () => {
    setOpen(false);
  };

  const navigateToProviderSearch = () => {
    window.open(EndpointService(FIND_PROVIDER_URL, TARGET_BLANK));
  };

  return (
    <PrimaryDialog
      id={NO_MATCH_FOUND_MODAL}
      open={open}
      title={intl.formatMessage({ id: NO_MATCH_FOUND_MODAL_TITLE })}
      titleIcon={<WarningCustomIcon id={WARNING_ICON_ID} />}
      actions={
        <Fragment>
          <StyledButton
            variant={CONTAINED}
            id={FIND_A_PROVIDER_BUTTON}
            onClick={navigateToProviderSearch}
          >
            {intl.formatMessage({ id: FIND_A_PROVIDER })}
          </StyledButton>
          <StyledButton
            variant={OUTLINED}
            id={
              flow === FLOW_TEXT_ACCOUNT
                ? CREATE_NEW_ACCOUNT_BUTTON
                : CREATE_NEW_APPLICATION_BUTTON
            }
            onClick={onCreateNewApplicationButtonClickHandler}
          >
            {intl.formatMessage({
              id:
                flow === FLOW_TEXT_ACCOUNT
                  ? CREATE_NEW_ACCOUNT_BUTTON
                  : CREATE_NEW_APPLICATION_BUTTON,
            })}
          </StyledButton>
          <StyledButton
            variant={TEXT}
            id={CANCEL_BUTTON}
            onClick={onCancelButtonClickHandler}
          >
            {intl.formatMessage({ id: CANCEL })}
          </StyledButton>
        </Fragment>
      }
    >
      <Stack spacing={2}>
        <Typography id={NO_MATCH_FOUND_MODAL_MODAL_CONTENT_1}>
          {intl.formatMessage(
            { id: NO_MATCH_FOUND_MODAL_MODAL_CONTENT_1 },
            {
              contactNumber: contactNumber,
              flowText: intl.formatMessage({ id: flowText }),
            }
          )}
        </Typography>
      </Stack>
    </PrimaryDialog>
  );
};

export default NoMatchFoundModal;

NoMatchFoundModal.propTypes = {
  /**
   * represent whether the modal is opening in "Forgot PW" flow (account) or "link an application flow" (application)
   */
  flow: PropTypes.string.isRequired,
  /**
   * Boolean flag to show the modal/dialog
   */
  open: PropTypes.bool.isRequired,
  /**
   * Setter method to update the boolean flag
   */
  setOpen: PropTypes.func.isRequired,
};
